import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase';

// Fantasy Team Service for Firestore operations
export const fantasyTeamService = {
  /**
   * Create a new fantasy team
   * @param {string} userId - User's Firebase UID
   * @param {Object} teamData - Team data (drivers, constructor, budget)
   * @returns {Promise<Object>} Created team data
   */
  createTeam: async (userId, teamData) => {
    try {
      const { driver_1, driver_2, driver_3, driver_4, constructor, budgetRemaining } = teamData;

      // Validate required fields
      if (!driver_1 || !driver_2 || !driver_3 || !driver_4 || !constructor) {
        throw new Error('All drivers and constructor must be selected');
      }

      // Client-side budget validation
      if (budgetRemaining < 0) {
        throw new Error('Budget exceeded! Please adjust your selections.');
      }

      const teamRef = doc(collection(db, 'fantasyTeams'));
      const teamDoc = {
        userId,
        driver_1,
        driver_2,
        driver_3,
        driver_4,
        constructor,
        budgetRemaining,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(teamRef, teamDoc);

      console.log('✅ Fantasy team created:', teamRef.id);
      
      return { id: teamRef.id, ...teamDoc };
    } catch (error) {
      console.error('❌ Error creating fantasy team:', error);
      throw error;
    }
  },

  /**
   * Get user's current fantasy team
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Object|null>} Team data or null
   */
  getTeam: async (userId) => {
    try {
      // Query for user's most recent team
      const teamsQuery = query(
        collection(db, 'fantasyTeams'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(teamsQuery);

      if (querySnapshot.empty) {
        console.log('ℹ️ No team found for user:', userId);
        return null;
      }

      const teamDoc = querySnapshot.docs[0];
      const teamData = { id: teamDoc.id, ...teamDoc.data() };
      
      console.log('✅ Team retrieved for user:', userId);
      return teamData;
    } catch (error) {
      console.error('❌ Error getting fantasy team:', error);
      throw error;
    }
  },

  /**
   * Update user's fantasy team
   * @param {string} teamId - Team document ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<boolean>} Success status
   */
  updateTeam: async (teamId, updates) => {
    try {
      const teamRef = doc(db, 'fantasyTeams', teamId);
      
      // Validate budget if updating team composition
      if ('budgetRemaining' in updates && updates.budgetRemaining < 0) {
        throw new Error('Budget exceeded! Please adjust your selections.');
      }

      await updateDoc(teamRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      console.log('✅ Fantasy team updated:', teamId);
      return true;
    } catch (error) {
      console.error('❌ Error updating fantasy team:', error);
      throw error;
    }
  },

  /**
   * Get team by ID
   * @param {string} teamId - Team document ID
   * @returns {Promise<Object|null>} Team data or null
   */
  getTeamById: async (teamId) => {
    try {
      const teamRef = doc(db, 'fantasyTeams', teamId);
      const teamDoc = await getDoc(teamRef);

      if (!teamDoc.exists()) {
        console.log('ℹ️ Team not found:', teamId);
        return null;
      }

      return { id: teamDoc.id, ...teamDoc.data() };
    } catch (error) {
      console.error('❌ Error getting team by ID:', error);
      throw error;
    }
  },

  /**
   * Validate team selection (client-side)
   * @param {Object} teamData - Team data to validate
   * @returns {Object} Validation result
   */
  validateTeam: (teamData) => {
    const errors = [];

    // Check all drivers are selected
    if (!teamData.driver_1) errors.push('Driver 1 is required');
    if (!teamData.driver_2) errors.push('Driver 2 is required');
    if (!teamData.driver_3) errors.push('Driver 3 is required');
    if (!teamData.driver_4) errors.push('Driver 4 is required');

    // Check no duplicate drivers
    const drivers = [
      teamData.driver_1, 
      teamData.driver_2, 
      teamData.driver_3, 
      teamData.driver_4
    ].filter(Boolean);
    
    if (new Set(drivers).size !== drivers.length) {
      errors.push('Cannot select the same driver multiple times');
    }

    // Check constructor is selected
    if (!teamData.constructor) {
      errors.push('Constructor is required');
    }

    // Check budget
    if (teamData.budgetRemaining < 0) {
      errors.push('Budget exceeded');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

export default fantasyTeamService;

